import React from 'react';
import {
  EmojiEvents as ContestIcon,
  Handshake as SponsorIcon,
  Insights as RankingsIcon,
  MenuBook as RubricIcon,
  School as CoachIcon,
  SmartDisplay as LearningIcon,
  SportsScore as TournamentIcon,
  TrendingUp as CompareIcon,
} from '@mui/icons-material';

export interface PlatformFeature {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  gradient: string;
  accent: string;
  glow: string;
  icon: React.ReactNode;
  chips: string[];
  cta: string;
  path: string;
  image?: string;
}

export const PLATFORM_FEATURES: PlatformFeature[] = [
  {
    id: 'rubrics',
    title: 'Sport Rubrics',
    subtitle: 'Structured mastery',
    description: 'Skills, strokes, drills and fitness frameworks per sport — regular and para pathways where applicable.',
    gradient: 'linear-gradient(145deg, #0f4c81 0%, #1d6eb5 55%, #0ea5e9 100%)',
    accent: '#7dd3fc',
    glow: 'rgba(14, 165, 233, 0.35)',
    icon: <RubricIcon sx={{ fontSize: 32 }} />,
    chips: ['200+ skills', 'Drills & fitness', 'Para support'],
    cta: 'Explore rubrics',
    path: '/sport/badminton',
  },
  {
    id: 'learning',
    title: 'Learning Journeys',
    subtitle: 'Video-led growth',
    description: 'Training videos, self-practice, strength, mental health, STEM and backyard practice content.',
    gradient: 'linear-gradient(145deg, #1e3a5f 0%, #2563eb 50%, #6366f1 100%)',
    accent: '#a5b4fc',
    glow: 'rgba(99, 102, 241, 0.35)',
    icon: <LearningIcon sx={{ fontSize: 32 }} />,
    chips: ['Reference videos', 'Self-practice', 'STEM modules'],
    cta: 'Start learning',
    path: '/sport/badminton',
  },
  {
    id: 'rankings',
    title: 'Player Rankings',
    subtitle: 'Measure progress',
    description: 'Self, coach/academy and EncycGames evaluations with scorecards, filters and downloadable reports.',
    gradient: 'linear-gradient(145deg, #14532d 0%, #15803d 45%, #22c55e 100%)',
    accent: '#86efac',
    glow: 'rgba(34, 197, 94, 0.35)',
    icon: <RankingsIcon sx={{ fontSize: 32 }} />,
    chips: ['3 evaluators', 'Auto-save', 'PDF reports'],
    cta: 'View rankings',
    path: '/sport/badminton',
  },
  {
    id: 'contests',
    title: 'Contests',
    subtitle: 'Perform & compete',
    description: 'Skills, drills, fitness, fun format and creative contests with video submissions and leaderboards.',
    gradient: 'linear-gradient(145deg, #7c2d12 0%, #ea580c 50%, #f59e0b 100%)',
    accent: '#fcd34d',
    glow: 'rgba(245, 158, 11, 0.4)',
    icon: <ContestIcon sx={{ fontSize: 32 }} />,
    chips: ['5 formats', 'Video submit', 'Leaderboard'],
    cta: 'Browse contests',
    path: '/sport/badminton',
    image: '/images/Badminton-Contest.jpg',
  },
  {
    id: 'coach',
    title: 'Coach & Academy',
    subtitle: 'Grow your cohort',
    description: 'Track students, assign homework, manage academies, certificates and junior coach permissions.',
    gradient: 'linear-gradient(145deg, #312e81 0%, #4f46e5 55%, #7c3aed 100%)',
    accent: '#c4b5fd',
    glow: 'rgba(124, 58, 237, 0.35)',
    icon: <CoachIcon sx={{ fontSize: 32 }} />,
    chips: ['Student tracking', 'Homework', 'Academy hub'],
    cta: 'Register as coach',
    path: '/register/coach',
  },
  {
    id: 'compare',
    title: 'Compare & Reports',
    subtitle: 'Benchmark peers',
    description: 'Compare training benchmarks across players and generate performance reports for coaches and parents.',
    gradient: 'linear-gradient(145deg, #134e4a 0%, #0d9488 50%, #14b8a6 100%)',
    accent: '#5eead4',
    glow: 'rgba(20, 184, 166, 0.35)',
    icon: <CompareIcon sx={{ fontSize: 32 }} />,
    chips: ['Peer charts', 'Training trends', 'Export PDF'],
    cta: 'Sign in to compare',
    path: '/login',
  },
  {
    id: 'tournaments',
    title: 'Tournaments & Leagues',
    subtitle: 'Team competition',
    description: 'Leagues, fixtures, milestones and tournament moments — expanding across sports on the platform.',
    gradient: 'linear-gradient(145deg, #1e293b 0%, #334155 50%, #475569 100%)',
    accent: '#cbd5e1',
    glow: 'rgba(71, 85, 105, 0.35)',
    icon: <TournamentIcon sx={{ fontSize: 32 }} />,
    chips: ['Team leagues', 'Milestones', 'Fixtures'],
    cta: 'Explore tournaments',
    path: '/sport/badminton',
  },
  {
    id: 'sponsors',
    title: 'Sponsor Partnerships',
    subtitle: 'Brand with impact',
    description: 'Multi-tier sponsorship for contests, training videos and content — global brands, India-first rollout.',
    gradient: 'linear-gradient(145deg, #0b1220 0%, #1e1b4b 45%, #312e81 100%)',
    accent: '#fbbf24',
    glow: 'rgba(251, 191, 36, 0.3)',
    icon: <SponsorIcon sx={{ fontSize: 32 }} />,
    chips: ['Contest branding', 'No public pricing', 'Flexible tiers'],
    cta: 'Partner with us',
    path: '/sponsors',
  },
];
